/**
 * Swap Token Selector
 *
 * Token selector for Jupiter swaps. Shows user's held tokens first,
 * then allows searching all Jupiter verified tokens.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Search, Coins, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { WalletToken } from '@/hooks/useTokenBalances';

// SOL native mint constant
const SOL_MINT = 'So11111111111111111111111111111111111111112';
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

export interface SwapToken {
  mint: string;
  symbol: string;
  name: string;
  logoURI: string | null;
  decimals: number;
  balance?: number;
}

// Well-known tokens for instant defaults
export const SOL_TOKEN: SwapToken = {
  mint: SOL_MINT,
  symbol: 'SOL',
  name: 'Solana',
  logoURI: '/solana-logo.svg',
  decimals: 9,
};

export const USDC_TOKEN: SwapToken = {
  mint: USDC_MINT,
  symbol: 'USDC',
  name: 'USD Coin',
  logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
  decimals: 6,
};

interface SwapTokenSelectorProps {
  label: string;
  selected: SwapToken | null;
  onSelect: (token: SwapToken) => void;
  userTokens: WalletToken[];
  solBalance: string;
  disabled?: boolean;
  excludeMint?: string;
}

// Convert WalletToken to SwapToken
function walletToSwapToken(token: WalletToken): SwapToken {
  return {
    mint: token.mint,
    symbol: token.symbol,
    name: token.name,
    logoURI: token.imageUrl,
    decimals: token.decimals,
    balance: token.uiAmount,
  };
}

export const SwapTokenSelector = ({
  label,
  selected,
  onSelect,
  userTokens,
  solBalance,
  disabled,
  excludeMint,
}: SwapTokenSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SwapToken[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Debounced search
  const doSearch = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    try {
      const { data, error } = await supabase.functions.invoke('jupiter-swap', {
        body: { operation: 'search-tokens', query },
      });

      if (error) {
        console.warn('[SwapTokenSelector] Search error:', error);
        setSearchResults([]);
        return;
      }

      // Jupiter tokens/v2/search returns an array of token objects
      const tokens: SwapToken[] = (Array.isArray(data) ? data : [])
        .filter((t: Record<string, unknown>) => t.address && t.symbol)
        .map((t: Record<string, unknown>) => ({
          mint: t.address as string,
          symbol: t.symbol as string,
          name: (t.name as string) || (t.symbol as string),
          logoURI: (t.logoURI as string) || null,
          decimals: (t.decimals as number) || 9,
        }));

      setSearchResults(tokens);
    } catch (err) {
      console.warn('[SwapTokenSelector] Search failed:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => doSearch(value), 300);
  };

  // Build user token list (SOL first, then SPL, exclude the other selector's mint)
  const userSwapTokens: SwapToken[] = (() => {
    const sol: SwapToken = {
      ...SOL_TOKEN,
      balance: parseFloat(solBalance) || 0,
    };

    const splTokens = userTokens
      .filter((t) => t.mint !== excludeMint)
      .map(walletToSwapToken);

    const result: SwapToken[] = [];
    if (sol.mint !== excludeMint) result.push(sol);
    result.push(...splTokens);
    return result;
  })();

  // Filter user tokens by search query
  const filteredUserTokens = searchQuery
    ? userSwapTokens.filter(
        (t) =>
          t.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.mint.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : userSwapTokens;

  // Filter search results to exclude already-shown user tokens
  const userMints = new Set(userSwapTokens.map((t) => t.mint));
  const filteredSearchResults = searchResults.filter(
    (t) => !userMints.has(t.mint) && t.mint !== excludeMint
  );

  const handleSelect = (token: SwapToken) => {
    onSelect(token);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const renderTokenIcon = (token: SwapToken, size = 'w-6 h-6') => {
    if (token.logoURI) {
      if (token.mint === SOL_MINT) {
        return (
          <div className={`${size} rounded-full bg-black flex items-center justify-center overflow-hidden flex-shrink-0`}>
            <img loading="lazy" src={token.logoURI} alt={token.symbol} className="w-4 h-4" />
          </div>
        );
      }
      return (
        <img
          loading="lazy"
          src={token.logoURI}
          alt={token.symbol}
          className={`${size} rounded-full object-cover flex-shrink-0`}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      );
    }
    return (
      <div className={`${size} rounded-full bg-muted flex items-center justify-center flex-shrink-0`}>
        <Coins className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-border transition-colors w-full ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-muted/30 cursor-pointer'
        } ${selected ? '' : 'border-dashed'}`}
      >
        {selected ? (
          <>
            {renderTokenIcon(selected)}
            <span className="text-sm font-medium">{selected.symbol}</span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground">Select coin</span>
        )}
        <ChevronDown
          className={`w-3.5 h-3.5 text-muted-foreground ml-auto transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-full min-w-[280px] max-h-72 rounded-lg border border-border bg-secondary/95 backdrop-blur-sm shadow-lg flex flex-col">
          {/* Search input */}
          <div className="p-2 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/30">
              <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search coins..."
                className="bg-transparent text-sm outline-none w-full placeholder:text-muted-foreground"
              />
              {isSearching && (
                <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin flex-shrink-0" />
              )}
            </div>
          </div>

          {/* Token list */}
          <div className="overflow-y-auto flex-1">
            {/* User tokens section */}
            {filteredUserTokens.length > 0 && (
              <>
                {searchQuery && (
                  <div className="px-3 pt-2 pb-1">
                    <span className="text-xs text-muted-foreground font-medium">Your coins</span>
                  </div>
                )}
                {filteredUserTokens.map((token) => (
                  <button
                    key={token.mint}
                    type="button"
                    onClick={() => handleSelect(token)}
                    className={`w-full flex items-center gap-3 px-3 min-h-[44px] transition-colors hover:bg-accent/10 active:bg-accent/20 cursor-pointer ${
                      selected?.mint === token.mint ? 'bg-muted/30' : ''
                    }`}
                  >
                    {renderTokenIcon(token)}
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <span className="text-sm font-medium truncate">{token.symbol}</span>
                      <span className="text-xs text-muted-foreground truncate">{token.name}</span>
                    </div>
                    {token.balance !== undefined && (
                      <span className="text-sm text-muted-foreground flex-shrink-0">
                        {token.balance < 0.001 && token.balance > 0
                          ? '<0.001'
                          : token.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                      </span>
                    )}
                  </button>
                ))}
              </>
            )}

            {/* Search results section */}
            {filteredSearchResults.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1">
                  <span className="text-xs text-muted-foreground font-medium">All coins</span>
                </div>
                {filteredSearchResults.map((token) => (
                  <button
                    key={token.mint}
                    type="button"
                    onClick={() => handleSelect(token)}
                    className="w-full flex items-center gap-3 px-3 min-h-[44px] transition-colors hover:bg-accent/10 active:bg-accent/20 cursor-pointer"
                  >
                    {renderTokenIcon(token)}
                    <div className="flex flex-col items-start min-w-0 flex-1">
                      <span className="text-sm font-medium truncate">{token.symbol}</span>
                      <span className="text-xs text-muted-foreground truncate">{token.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {token.mint.slice(0, 4)}...{token.mint.slice(-4)}
                    </span>
                  </button>
                ))}
              </>
            )}

            {/* Empty state */}
            {filteredUserTokens.length === 0 && filteredSearchResults.length === 0 && !isSearching && searchQuery && (
              <div className="px-3 py-4 text-center">
                <span className="text-sm text-muted-foreground">No coins found</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
